import React, { useEffect, useState } from 'react';
import { AppProvider } from '@/contexts/AppContext';
import { ChatProvider } from '@/contexts/ChatContext';
import { GameInstanceProvider } from '@/contexts/GameInstanceContext';
import { VideoProvider } from '@/contexts/VideoContext';
import { VideoViewProvider } from '@/contexts/VideoViewContext';
import { EditModeProvider } from '@/contexts/EditModeContext';
import { useAppContext } from '@/hooks/useAppContext';
import { PlatformAdminView } from '@/components/admin';
import { AuthModal, ConfirmEmailLanding, ResetPasswordLanding } from '@/components/auth';
import { InviteLanding } from '@/components/invite';
import { RoomSelection } from '@/components/room';
import GameUI from '@/components/game/GameUI';
import { DecorationPaletteSync } from '@/components/decorations/DecorationPaletteSync';
import {
  CONFIRM_EMAIL_PATH,
  INVITE_PATH,
  RESET_PASSWORD_PATH,
  readConfirmEmailTokenFrom,
  readInviteTokenFrom,
  readResetTokenFrom
} from '@/utils/app-links';

// Import styles
import '@/game/game.css';

/**
 * There is no router. Views are a string union so the conditional rendering
 * below stays one `if` per view.
 */
type AppView = 'rooms' | 'game' | 'invite' | 'resetPassword' | 'confirmEmail' | 'platformAdmin';

/**
 * `/invite/<token>`, `/reset-password#<token>` and `/confirm-email#<token>`
 * are the only addresses the SPA reads. Reading them belongs in the
 * state initializer — it must happen before the first render — but rewriting
 * the URL does not: `history.replaceState` is a side effect on the document,
 * and React may call an initializer more than once. So the token is read here
 * and the address bar is cleaned up in an effect, which leaves a refresh
 * landing on `/` rather than re-entering the flow with a spent token.
 *
 * The reset and email-confirmation tokens ride in the fragment, which the browser never sends: it
 * stays out of every access log between here and the server, and out of
 * Referer headers.
 */
const readInviteToken = () => readInviteTokenFrom(window.location);
const readResetToken = () => readResetTokenFrom(window.location);
const readConfirmEmailToken = () => readConfirmEmailTokenFrom(window.location);

// Main App Component
const App: React.FC = React.memo(() => {
  const { auth, roomConnection, platformAdmin } = useAppContext();
  const { isAuthenticated, user } = auth;

  // Read once, on the first render only.
  const [inviteToken, setInviteToken] = useState<string | null>(readInviteToken);

  const [resetToken, setResetToken] = useState<string | null>(readResetToken);
  const [confirmEmailToken, setConfirmEmailToken] = useState<string | null>(readConfirmEmailToken);
  // A dead reset link leads to the "Forgot password?" form, not the sign-in one.
  const [authMode, setAuthMode] = useState<'login' | 'forgot'>('login');

  useEffect(() => {
    if (inviteToken && INVITE_PATH.test(window.location.pathname)) {
      window.history.replaceState({}, '', '/');
    }
  }, [inviteToken]);

  // Only the next sign-in screen opens on "Forgot password?", not every later one.
  useEffect(() => {
    if (isAuthenticated) setAuthMode('login');
  }, [isAuthenticated]);

  // The reset token must not linger in the address bar or the history.
  useEffect(() => {
    if (resetToken && RESET_PASSWORD_PATH.test(window.location.pathname)) {
      window.history.replaceState({}, '', '/');
    }
  }, [resetToken]);

  // Nor the email confirmation token: it is single-use either way.
  useEffect(() => {
    if (confirmEmailToken && CONFIRM_EMAIL_PATH.test(window.location.pathname)) {
      window.history.replaceState({}, '', '/');
    }
  }, [confirmEmailToken]);

  // The console wins over the island: the in-game entry point leaves the
  // island before opening it, but a reconnect that lands afterwards must not
  // pull the operator back into the game.
  const view: AppView = inviteToken
    ? 'invite'
    : resetToken
      ? 'resetPassword'
      : confirmEmailToken
        ? 'confirmEmail'
        : platformAdmin.isOpen
          ? 'platformAdmin'
          : roomConnection.currentRoom && user
            ? 'game'
            : 'rooms';

  // Simplified debug logging - only log routing decisions
  // (Removed verbose state logging to reduce console noise)

  // The invite landing runs before authentication: the invitee may have no
  // account yet, and registering is how they get one.
  if (view === 'invite' && inviteToken) {
    return (
      <div className="app">
        <InviteLanding token={inviteToken} onAccepted={() => setInviteToken(null)} />
      </div>
    );
  }

  // Like the invite landing, the reset page runs before authentication: being
  // unable to sign in is why the user is here.
  if (view === 'resetPassword' && resetToken) {
    return (
      <div className="app">
        <ResetPasswordLanding
          token={resetToken}
          onDone={() => setResetToken(null)}
          onRequestNewLink={() => {
            setAuthMode('forgot');
            setResetToken(null);
          }}
        />
      </div>
    );
  }

  // The confirmation link may be opened in any browser, signed in or not, so
  // the landing renders before the sign-in gate. Confirming takes the session
  // of the account that asked: the landing signs the user in first if needed.
  if (view === 'confirmEmail' && confirmEmailToken) {
    return (
      <div className="app">
        <ConfirmEmailLanding token={confirmEmailToken} onDone={() => setConfirmEmailToken(null)} />
      </div>
    );
  }

  // Simple routing based on authentication and room state
  if (!isAuthenticated) {
    return (
      <div className="app">
        <AuthModal key={authMode} initialMode={authMode} />
      </div>
    );
  }
  
  // The console's own gate decides what a non-superadmin sees here; opening it
  // is a piece of app state, and being allowed in is not.
  if (view === 'platformAdmin') {
    return (
      <div className="app">
        <PlatformAdminView onClose={platformAdmin.close} />
      </div>
    );
  }

  // Simplified routing - prefer roomConnection.currentRoom since it's more reliable
  if (view === 'game') {
    // console.info('🎮 App: Routing to GameUI'); // Reduced logging
    return (
      <div className="app">
        <GameUI roomConnection={roomConnection} />
      </div>
    );
  }
  // console.info('🏠 App: Routing to RoomSelection'); // Reduced logging
  return (
    <div className="app">
      <RoomSelection />
    </div>
  );
});

// Wrapped App with Provider
const AppWithProvider: React.FC = () => {
  return (
    <GameInstanceProvider>
      <AppProvider>
        {/* The island's one chat: mounted with the socket rather than with the
            game view, so its listeners are in place before the island join's
            chat:initial_state arrives */}
        <ChatProvider>
          <VideoProvider>
            {/* The corner tiles or full screen: this viewer's view of the call */}
            <VideoViewProvider>
              <EditModeProvider>
                <DecorationPaletteSync />
                <App />
              </EditModeProvider>
            </VideoViewProvider>
          </VideoProvider>
        </ChatProvider>
      </AppProvider>
    </GameInstanceProvider>
  );
};

export default AppWithProvider;